import { useEffect, useState } from 'react';
import { FREE_PLANET_IMAGE_COUNT, TOTAL_PLANET_IMAGE_COUNT, PREMIUM_PLANET_IMAGE_PRICE } from '@kaeliux/shared';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { usePlayerStore } from '../store/playerStore';
import { unlockPlanetImage } from '../services/premium.service';

export interface PlanetImagePickerModalProps {
    open: boolean;
    currentPlanetType: number;
    onSelect: (planetType: number) => void;
    onCancel: () => void;
}

const SLIDE_SPACING_PX = 88;
const MAX_VISIBLE_OFFSET = 3;

function planetImageUrl(planetType: number): string {
    return new URL(`../assets/images/planets/planet_${planetType}.png`, import.meta.url).href;
}

/** A premium image (id above the free range) is locked until the player has unlocked it with Dark Matter. */
function imageLocked(planetType: number, unlockedImages: number[]): boolean {
    return planetType > FREE_PLANET_IMAGE_COUNT && !unlockedImages.includes(planetType);
}

function LockIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-space-200 drop-shadow" aria-hidden="true">
            <rect x="5" y="11" width="14" height="9" rx="2" fill="currentColor" fillOpacity="0.9" />
            <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
        </svg>
    );
}

export default function PlanetImagePickerModal({
    open,
    currentPlanetType,
    onSelect,
    onCancel,
}: PlanetImagePickerModalProps) {
    const [centerIndex, setCenterIndex] = useState(currentPlanetType - 1);
    const [unlockBusy, setUnlockBusy] = useState(false);
    const [unlockError, setUnlockError] = useState<string | null>(null);
    const premium = usePlayerStore((s) => s.premium);
    const unlockedImages = premium?.unlockedPlanetImages ?? [];
    const isLocked = (planetType: number) => imageLocked(planetType, unlockedImages);

    useEffect(() => {
        if (open) {
            setCenterIndex(currentPlanetType - 1);
            setUnlockError(null);
        }
    }, [open, currentPlanetType]);

    useEffect(() => {
        if (!open) return;
        function onKeyDown(e: KeyboardEvent) {
            if (e.key === 'ArrowLeft') setCenterIndex((i) => Math.max(0, i - 1));
            if (e.key === 'ArrowRight') setCenterIndex((i) => Math.min(TOTAL_PLANET_IMAGE_COUNT - 1, i + 1));
        }
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open]);

    const centerPlanetType = centerIndex + 1;
    const centerLocked = isLocked(centerPlanetType);

    function confirmSelection() {
        if (isLocked(centerPlanetType)) return;
        onSelect(centerPlanetType);
    }

    async function handleUnlock() {
        setUnlockBusy(true);
        setUnlockError(null);
        try {
            const { premium: next } = await unlockPlanetImage(centerPlanetType);
            usePlayerStore.setState({ premium: next });
        } catch (e) {
            setUnlockError((e as Error)?.message ?? 'Could not unlock this image');
        } finally {
            setUnlockBusy(false);
        }
    }

    function handleSlideClick(index: number) {
        if (index === centerIndex) {
            confirmSelection();
        } else {
            setCenterIndex(index);
        }
    }

    const slides = Array.from({ length: TOTAL_PLANET_IMAGE_COUNT }, (_, i) => i);

    return (
        <Modal open={open} onClose={onCancel} title="Choose a planet image" size="lg">
            <div className="flex flex-col gap-4">
                <div className="flex items-baseline justify-between gap-2">
                    <span className="text-micro text-content-muted">
                        {centerPlanetType} / {TOTAL_PLANET_IMAGE_COUNT}
                        {centerLocked && ' · Locked'}
                    </span>
                </div>

                <div className="relative flex items-center">
                    <button
                        type="button"
                        onClick={() => setCenterIndex((i) => Math.max(0, i - 1))}
                        disabled={centerIndex === 0}
                        aria-label="Previous image"
                        className="absolute left-0 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-space-700/80 text-space-200 hover:bg-space-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                        ‹
                    </button>

                    <div className="relative h-40 w-full overflow-hidden" style={{ perspective: '900px' }}>
                        {slides.map((index) => {
                            const planetType = index + 1;
                            const offset = index - centerIndex;
                            const abs = Math.abs(offset);
                            const visible = abs <= MAX_VISIBLE_OFFSET;
                            const locked = isLocked(planetType);
                            const isCenter = offset === 0;
                            const scale = Math.max(0.5, 1 - abs * 0.15);
                            const translateX = offset * SLIDE_SPACING_PX;
                            const rotateY = Math.max(-65, Math.min(65, offset * -25));
                            const opacity = visible ? Math.max(0, 1 - abs * 0.25) : 0;

                            return (
                                <button
                                    key={planetType}
                                    type="button"
                                    onClick={() => handleSlideClick(index)}
                                    aria-label={`Planet image ${planetType}${locked ? ' (locked)' : ''}`}
                                    aria-pressed={planetType === currentPlanetType}
                                    tabIndex={visible ? 0 : -1}
                                    style={{
                                        transform: `translate(-50%, -50%) translateX(${translateX}px) scale(${scale}) rotateY(${rotateY}deg)`,
                                        opacity,
                                        zIndex: 50 - abs,
                                    }}
                                    className={`absolute left-1/2 top-1/2 w-28 h-28 rounded-control overflow-hidden border-2 transition-[transform,opacity] duration-300 ease-out ${!visible ? 'pointer-events-none' : ''
                                        } ${isCenter && planetType === currentPlanetType
                                            ? 'border-status-info'
                                            : isCenter
                                                ? 'border-space-400'
                                                : 'border-transparent'
                                        }`}
                                >
                                    <img
                                        src={planetImageUrl(planetType)}
                                        alt={`Planet image ${planetType}`}
                                        className={`w-full h-full object-cover ${locked ? 'grayscale brightness-50' : ''}`}
                                    />
                                    {locked && (
                                        <span className="absolute inset-0 flex items-center justify-center bg-space-950/60">
                                            <LockIcon />
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    <button
                        type="button"
                        onClick={() => setCenterIndex((i) => Math.min(TOTAL_PLANET_IMAGE_COUNT - 1, i + 1))}
                        disabled={centerIndex === TOTAL_PLANET_IMAGE_COUNT - 1}
                        aria-label="Next image"
                        className="absolute right-0 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-space-700/80 text-space-200 hover:bg-space-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                        ›
                    </button>
                </div>

                <div className="flex flex-wrap justify-center gap-1.5 px-2">
                    {slides.map((index) => {
                        const planetType = index + 1;
                        const locked = isLocked(planetType);
                        const isCenter = index === centerIndex;
                        return (
                            <button
                                key={planetType}
                                type="button"
                                onClick={() => setCenterIndex(index)}
                                aria-label={`Go to planet image ${planetType}`}
                                aria-current={isCenter}
                                className={`h-2 rounded-full transition-all ${isCenter
                                    ? 'w-6 bg-content-primary'
                                    : locked
                                        ? 'w-2 bg-space-600 hover:bg-space-500'
                                        : 'w-2 bg-space-500 hover:bg-space-400'
                                    }`}
                            />
                        );
                    })}
                </div>

                {unlockError && (
                    <p className="text-status-danger text-body text-center" role="alert">{unlockError}</p>
                )}

                <div className="flex gap-3 justify-end mt-2">
                    <Button type="button" variant="secondary" size="sm" onClick={onCancel}>
                        Cancel
                    </Button>
                    {centerLocked ? (
                        <Button type="button" variant="accent" size="sm" loading={unlockBusy} onClick={handleUnlock}>
                            {unlockBusy ? 'Unlocking…' : `Unlock for ${PREMIUM_PLANET_IMAGE_PRICE} Dark Matter`}
                        </Button>
                    ) : (
                        <Button type="button" variant="primary" size="sm" onClick={confirmSelection}>
                            Select this image
                        </Button>
                    )}
                </div>
            </div>
        </Modal>
    );
}
